import React, { useState } from "react";
import {
  UserPlus,
  Zap,
  Sparkles,
  MailCheck,
  ArrowUpRight,
  Lock,
} from "lucide-react";
import { AuthModal } from "@/components/AuthModal";
import { useAuth } from "@/context/AuthContext";

/**
 * MembershipCallout — auth-aware promotional block encouraging visitors to
 * create a free OGO Community account for 1-click downloads and future
 * members-only content. Renders nothing for signed-in users.
 *
 *   variant="hero"   Full-width, home-page banner with three benefit chips.
 *   variant="bar"    Compact horizontal bar for section transitions.
 *   variant="pill"   Inline nudge used inside dense card grids.
 */
export function MembershipCallout({ variant = "hero", testid, className = "" }) {
  const { isAuthed } = useAuth();
  const [open, setOpen] = useState(false);
  const [initialTab, setInitialTab] = useState("register");

  if (isAuthed) return null;

  const openRegister = () => {
    setInitialTab("register");
    setOpen(true);
  };
  const openLogin = () => {
    setInitialTab("login");
    setOpen(true);
  };

  if (variant === "bar") {
    return (
      <>
        <div
          data-testid={testid || "membership-callout-bar"}
          className={`border border-[#D4AF37]/40 bg-[#0A1020] px-6 sm:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 ${className}`}
        >
          <div className="flex items-start sm:items-center gap-4">
            <div className="w-11 h-11 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] shrink-0">
              <UserPlus className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">
                Free OGO Community · Members Get More
              </div>
              <p className="text-slate-300 text-sm leading-relaxed max-w-xl">
                Skip the form on every download. Members get{" "}
                <span className="text-white font-semibold">
                  1-click access
                </span>{" "}
                to every current & future resource — plus early access to
                members-only guides, blogs, and training.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={openRegister}
              data-testid={`${testid || "membership-callout-bar"}-register`}
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#142339] px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
            >
              Join Free
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={openLogin}
              className="text-[10px] uppercase tracking-[0.22em] text-slate-400 hover:text-[#D4AF37]"
            >
              Sign in
            </button>
          </div>
        </div>
        <AuthModal open={open} onClose={() => setOpen(false)} initialTab={initialTab} />
      </>
    );
  }

  if (variant === "pill") {
    return (
      <>
        <button
          type="button"
          onClick={openRegister}
          data-testid={testid || "membership-callout-pill"}
          className={`inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors ${className}`}
        >
          <Zap className="w-3 h-3" />
          Join free — 1-click downloads
        </button>
        <AuthModal open={open} onClose={() => setOpen(false)} initialTab={initialTab} />
      </>
    );
  }

  // hero variant (default) — the marquee call-out.
  return (
    <>
      <section
        data-testid={testid || "membership-callout-hero"}
        className={`relative overflow-hidden border-y border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1020] via-[#0F1B2E] to-[#0A1020] py-16 sm:py-20 ${className}`}
      >
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#D4AF37]/[0.06] blur-3xl pointer-events-none" />
        <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-[#D4AF37]/[0.04] blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold mb-5">
                <Sparkles className="w-3 h-3" />
                Free · No credit card · Cancel anytime
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
                Join the{" "}
                <span className="text-[#D4AF37]">OGO Community</span> — free
                access, forever.
              </h2>
              <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                Stop filling out the email form every time. Create a free
                member account and get{" "}
                <span className="text-white font-semibold">
                  1-click downloads
                </span>{" "}
                on every current and future OGO resource — plus early access to
                upcoming members-only guides, protocol blogs, and professional
                training as we release it.
              </p>

              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <BenefitChip
                  icon={Zap}
                  title="1-Click Downloads"
                  body="No more forms. One click, file opens."
                />
                <BenefitChip
                  icon={MailCheck}
                  title="Zero Email Spam"
                  body="We only email you when a real new resource drops."
                />
                <BenefitChip
                  icon={Lock}
                  title="Members-Only Content"
                  body="Early access to future pro-tier guides & blogs."
                />
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={openRegister}
                  data-testid="membership-callout-hero-register"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#142339] px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Create Free Account
                </button>
                <button
                  type="button"
                  onClick={openLogin}
                  data-testid="membership-callout-hero-signin"
                  className="text-[11px] uppercase tracking-[0.22em] text-slate-300 hover:text-[#D4AF37] font-bold"
                >
                  Already a member? Sign in →
                </button>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="border border-[#D4AF37]/30 bg-[#142339] p-8 relative">
                <div className="absolute -top-3 left-6 bg-[#0F1B2E] px-3 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] border border-[#D4AF37]/30">
                  Guest vs. Member
                </div>
                <ComparisonRow
                  label="Download a resource"
                  guest="Fill out name + email"
                  member="1 click, instant"
                />
                <ComparisonRow
                  label="Grab a second resource"
                  guest="Fill out the form again"
                  member="1 click, instant"
                />
                <ComparisonRow
                  label="Personal library"
                  guest="—"
                  member="Full download history"
                />
                <ComparisonRow
                  label="Future members-only guides"
                  guest="Not available"
                  member="Included"
                  last
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <AuthModal open={open} onClose={() => setOpen(false)} initialTab={initialTab} />
    </>
  );
}

function BenefitChip({ icon: Icon, title, body }) {
  return (
    <div className="border border-white/10 bg-[#142339]/60 p-4">
      <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold">
          {title}
        </span>
      </div>
      <p className="text-slate-400 text-xs leading-relaxed">{body}</p>
    </div>
  );
}

function ComparisonRow({ label, guest, member, last }) {
  return (
    <div className={`grid grid-cols-3 gap-3 py-3 ${last ? "" : "border-b border-white/10"}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 self-center">
        {label}
      </div>
      <div className="text-xs text-slate-400 self-center">{guest}</div>
      <div className="text-xs text-[#D4AF37] font-semibold self-center">
        {member}
      </div>
    </div>
  );
}
